<!-- Reusable metric row: label, used / limit, and a colored progress bar. -->
<ng-template #metricRow let-metric let-compact="compact">
  <div>
    <div
      class="tw-flex tw-justify-between tw-text-dark-light-dynamic tw-mb-1"
      [ngClass]="compact ? 'tw-text-xs' : 'tw-text-sm'">
      <span>{{ metric.label }}</span>
      <span class="tw-font-medium">
        {{ metric.used | number }}
        <ng-container *ngIf="!isUnlimited(metric.limit)">/ {{ metric.limit | number }}</ng-container>
        <ng-container *ngIf="isUnlimited(metric.limit)">/ Unlimited</ng-container>
      </span>
    </div>
    <div
      *ngIf="!hideProgressBar && !isUnlimited(metric.limit)"
      class="tw-w-full tw-rounded-full tw-bg-gray-300 dark:tw-bg-gray-600 tw-overflow-hidden"
      [ngClass]="compact ? 'tw-h-1.5' : 'tw-h-2'">
      <div
        class="tw-h-full tw-rounded-full tw-transition-all tw-duration-300"
        [ngClass]="barColor(percent(metric.used, metric.limit), metricRemaining(metric))"
        [style.width.%]="percent(metric.used, metric.limit)"></div>
    </div>
  </div>
</ng-template>

<!-- Shared usage popover (used by button + sidebar triggers) -->
<mat-menu
  #popoverMenu="matMenu"
  class="chat-usage-popover"
  xPosition="after"
  yPosition="above"
  [overlapTrigger]="false">
  <div (click)="$event.stopPropagation()" class="chat-usage-card tw-flex tw-flex-col tw-gap-4 tw-p-4">
    <!-- Header: title + plan badge -->
    <div class="tw-flex tw-items-center tw-justify-between">
      <span class="tw-text-base tw-font-semibold tw-text-white">Usage this month</span>
      <span class="chat-usage-plan-badge">{{ planLabel | uppercase }}</span>
    </div>

    <!-- One block per metered dimension -->
    <div *ngFor="let metric of cardMetrics" class="tw-flex tw-flex-col tw-gap-1.5">
      <div class="tw-flex tw-items-center tw-justify-between tw-text-sm">
        <span class="tw-text-gray-200">{{ metric.label }}</span>
        <span *ngIf="!isUnlimited(metric.limit)" class="tw-font-medium tw-text-white">
          {{ metric.used | number }}
          <span class="tw-text-gray-400">/ {{ metric.limit | number }}</span>
        </span>
        <span *ngIf="isUnlimited(metric.limit)" class="tw-font-medium tw-text-gray-300">Unlimited</span>
      </div>

      <!-- Progress meter only for metered dimensions -->
      <div
        *ngIf="!isUnlimited(metric.limit)"
        class="tw-w-full tw-h-2 tw-rounded-full tw-bg-gray-700 tw-overflow-hidden">
        <div
          class="tw-h-full tw-rounded-full tw-transition-all tw-duration-300"
          [ngClass]="meterColor(percent(metric.used, metric.limit), metricRemaining(metric))"
          [style.width.%]="percent(metric.used, metric.limit)"></div>
      </div>

      <span class="tw-text-xs tw-text-gray-500">
        <ng-container *ngIf="!isUnlimited(metric.limit)">
          {{ resetInDays === 0 ? 'Resets today' : 'Resets in ' + resetInDays + (resetInDays === 1 ? ' day' : ' days') }}
        </ng-container>
        <ng-container *ngIf="isUnlimited(metric.limit)">No cap on your plan</ng-container>
      </span>
    </div>

    <!-- Upgrade CTA (hidden on top-tier plans) -->
    <button *ngIf="upgradeCtaLabel" type="button" class="chat-usage-upgrade-btn" (click)="goToBilling()">
      <mat-icon class="tw-text-base tw-w-5 tw-h-5">bolt</mat-icon>
      {{ upgradeCtaLabel }}
    </button>
  </div>
</mat-menu>

<!-- ===== Button mode: compact trigger + popover ===== -->
<ng-container *ngIf="displayMode === 'button' && usage">
  <button
    type="button"
    (keydown)="handleKeydown($event)"
    [matMenuTriggerFor]="popoverMenu"
    [class]="
      transparentBackground
        ? 'tw-flex tw-flex-row tw-items-center tw-gap-2 tw-p-2 tw-rounded-lg dark:tw-text-white tw-cursor-pointer'
        : 'tw-flex tw-flex-row tw-items-center tw-gap-2 tw-bg-gray-200 dark:tw-text-white dark:tw-bg-gray-800 tw-p-2 tw-rounded-lg dark:hover:tw-bg-gray-900 focus:tw-outline-none tw-ring-2 tw-ring-gray-300 dark:tw-ring-gray-700 tw-cursor-pointer'
    ">
    <span *ngIf="!hideText" class="tw-flex tw-items-center tw-gap-1.5 tw-text-sm">
      <mat-icon class="tw-text-link-color-dynamic tw-text-base tw-w-5 tw-h-5">bolt</mat-icon>
      Usage
    </span>
    <div
      *ngIf="!hideProgressBar"
      class="tw-w-16 tw-h-1.5 tw-rounded-full tw-bg-gray-300 dark:tw-bg-gray-600 tw-overflow-hidden">
      <div
        class="tw-h-full tw-rounded-full"
        [ngClass]="barColor(overallPercent, lowestRemaining)"
        [style.width.%]="overallPercent"></div>
    </div>
  </button>
</ng-container>

<!-- ===== Sidebar mode: plan badge + usage gauge (left-nav pattern) ===== -->
<ng-container *ngIf="displayMode === 'sidebar'">
  <div
    class="tw-relative tw-border tw-border-solid tw-rounded-lg"
    [style.borderColor]="gaugeStroke()">
    <button
      mat-icon-button
      (keydown)="handleKeydown($event)"
      [matMenuTriggerFor]="popoverMenu"
      class="tw-text-link-color-dynamic hover:tw-text-link-color-dynamic-darker tw-transition-colors"
      [matTooltip]="planLabel + ' plan — query usage'"
      matTooltipPosition="left">
      <span
        class="tw-text-[11px] tw-leading-[12px] tw-font-medium tw-absolute -tw-top-[7px] tw-right-[6px] tw-inline-block tw-bg-[#111111] tw-px-[2px]"
        [style.color]="gaugeStroke()">
        {{ planLabel }}
      </span>
      <svg viewBox="0 0 36 36" width="22" height="22" aria-hidden="true">
        <circle
          cx="18"
          cy="18"
          r="15.9155"
          fill="none"
          [attr.stroke]="gaugeStroke()"
          stroke-opacity="0.25"
          stroke-width="3.5"></circle>
        <circle
          cx="18"
          cy="18"
          r="15.9155"
          fill="none"
          [attr.stroke]="gaugeStroke()"
          stroke-width="3.5"
          stroke-linecap="round"
          pathLength="100"
          [attr.stroke-dasharray]="gaugePercent + ', 100'"
          transform="rotate(-90 18 18)"></circle>
      </svg>
    </button>
  </div>
</ng-container>

<!-- ===== Inline mode: compact box ===== -->
<ng-container *ngIf="displayMode === 'inline' && usage">
  <div
    class="tw-text-dark-light-dynamic tw-flex tw-flex-col tw-gap-2.5 tw-p-3 tw-rounded-lg"
    [ngClass]="transparentBackground ? '' : 'tw-bg-gray-100 dark:tw-bg-gray-700'">
    <ng-container *ngFor="let metric of metrics">
      <ng-container
        *ngTemplateOutlet="metricRow; context: { $implicit: metric, compact: true }"></ng-container>
    </ng-container>
  </div>
</ng-container>

<!-- ===== Detailed mode: collapsible card ===== -->
<ng-container *ngIf="displayMode === 'detailed' && usage">
  <div
    class="tw-text-dark-light-dynamic tw-flex tw-flex-col tw-p-4 tw-rounded-lg"
    [ngClass]="[
      transparentBackground ? '' : 'tw-bg-gray-100 dark:tw-bg-gray-700',
      isDetailCollapsed ? 'tw-gap-0' : 'tw-gap-4'
    ]">
    <div class="tw-flex tw-items-center tw-justify-between">
      <div
        class="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer"
        (click)="toggleDetailCollapse()"
        role="button"
        tabindex="0"
        (keydown.enter)="toggleDetailCollapse()"
        (keydown.space)="toggleDetailCollapse(); $event.preventDefault()">
        <mat-icon class="tw-text-link-color-dynamic tw-text-xl tw-w-6 tw-h-6">bolt</mat-icon>
        <span class="tw-text-base tw-font-semibold">Query Usage This Cycle</span>
        <mat-icon
          class="tw-transition-transform tw-duration-300 tw-text-xl"
          [ngClass]="{ 'tw-rotate-180': isDetailCollapsed }">
          expand_more
        </mat-icon>
      </div>
    </div>

    <div
      class="tw-overflow-hidden tw-transition-all tw-duration-500"
      [ngClass]="
        isDetailCollapsed
          ? 'tw-max-h-0 tw-opacity-0'
          : 'tw-max-h-[400px] tw-opacity-100 tw-flex tw-flex-col tw-gap-3'
      ">
      <ng-container *ngFor="let metric of metrics">
        <ng-container
          *ngTemplateOutlet="metricRow; context: { $implicit: metric, compact: false }"></ng-container>
      </ng-container>
    </div>
  </div>
</ng-container>
